// My account: password and two-factor sign-in (an authenticator app on a phone).

import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { errorText } from "../hooks";

export function AccountPage() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState<{ secret: string; qr_svg: string } | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!user) return null;

  const run = async (f: () => Promise<unknown>, ok: string) => {
    setError(null);
    setMsg(null);
    try {
      await f();
      setMsg(ok);
      setCode("");
      setPassword("");
      await refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <>
      <h1>My account</h1>
      <div className="card">
        <p><strong>{user.username}</strong> ({user.role}) · <Link to="/password">Change password</Link></p>
      </div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Two-factor sign-in</h2>
        {user.must_setup_2fa && (
          <p className="warn">Your study requires two-factor sign-in for staff. Set it up to continue.</p>
        )}
        {user.totp_enabled ? (
          <>
            <p className="good">On. Signing in asks for a code from your authenticator app.</p>
            <div className="row">
              <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
              <label>Current code<input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} /></label>
              <button className="secondary" disabled={!password || code.length < 6}
                onClick={() => void run(() => api.twoFactorDisable(password, code), "Two-factor sign-in is off.")}>Turn off</button>
            </div>
          </>
        ) : !setup ? (
          <>
            <p>Adds a second step to signing in: a 6-digit code from an authenticator app on your phone
              (Google Authenticator, Microsoft Authenticator, 1Password and others).</p>
            <button onClick={() => void api.twoFactorSetup().then(setSetup, (e) => setError(errorText(e)))}>Turn on two-factor sign-in</button>
          </>
        ) : (
          <>
            <ol>
              <li>In your authenticator app, add an account and scan this code.</li>
              <li>Type the 6-digit code it shows, then press Confirm.</li>
            </ol>
            <img alt="QR code for your authenticator app" width={220} height={220}
              src={`data:image/svg+xml;base64,${btoa(setup.qr_svg)}`} />
            <p className="small muted">Can’t scan? Enter this key by hand: <code>{setup.secret.match(/.{1,4}/g)?.join(" ")}</code></p>
            <div className="row">
              <label>Code<input autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} /></label>
              <button disabled={code.length < 6} onClick={() => void run(async () => { await api.twoFactorEnable(code); setSetup(null); },
                "Two-factor sign-in is on.")}>Confirm</button>
            </div>
          </>
        )}
        {msg && <p className="good">{msg}</p>}
        {error && <p className="error">{error}</p>}
        <p className="small muted">Lost your phone? An admin can reset two-factor sign-in for your account.</p>
      </div>
    </>
  );
}
